/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

import React from "react"
import { TrackingProvider } from "./src/contexts/TrackingContext"
import { SegmentProvider } from "./src/contexts/SegmentContext"

// Hash-Anchor robust anspringen.
// Die Startseite rendert Services / Case Studies / Constellation erst client-seitig
// (window/DOMParser) und mit Bildern + GSAP — der Ziel-Abschnitt sitzt beim ersten
// nativen Sprung noch nicht an seiner endgültigen Position. Deshalb re-scrollen wir
// so lange nach, bis das Layout stabil ist (bricht ab, sobald der Nutzer selbst scrollt).
const scrollToHash = () => {
  if (typeof window === "undefined") return
  const raw = window.location.hash
  if (!raw || raw.length < 2) return

  let id
  try {
    id = decodeURIComponent(raw.slice(1))
  } catch (e) {
    id = raw.slice(1)
  }

  // Doppelte Läufe vermeiden (onInitialClientRender + onRouteUpdate feuern beim Start beide).
  if (window.__nvgHashScrollActive) return
  window.__nvgHashScrollActive = true

  let tries = 0
  let lastTop = null
  let stable = 0
  let aborted = false

  const cancel = () => {
    aborted = true
    window.__nvgHashScrollActive = false
    window.removeEventListener("wheel", cancel)
    window.removeEventListener("touchmove", cancel)
    window.removeEventListener("keydown", cancel)
  }
  window.addEventListener("wheel", cancel, { passive: true })
  window.addEventListener("touchmove", cancel, { passive: true })
  window.addEventListener("keydown", cancel)

  const attempt = () => {
    if (aborted) return cancel()
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "auto", block: "start" })
      const top = Math.round(el.getBoundingClientRect().top)
      if (lastTop !== null && Math.abs(top - lastTop) <= 1) {
        stable += 1
      } else {
        stable = 0
      }
      lastTop = top
      // Position hat sich mehrere Frames nicht mehr verschoben → fertig.
      if (stable >= 4) return cancel()
    }
    tries += 1
    if (tries < 45) {
      requestAnimationFrame(() => setTimeout(attempt, 60))
    } else {
      cancel()
    }
  }

  attempt()
}

// Initialize Umami tracking
export const onInitialClientRender = () => {
  const script = document.createElement("script");
  script.src = "https://cloud.umami.is/script.js";
  script.async = true;
  script.defer = true;
  script.setAttribute("data-website-id", "657be003-3b31-46e8-a0be-9d910816c92e");
  document.body.appendChild(script);

  // Erst nach dem Initial-Render greifen — dann läuft der Nachlauf über den
  // Hydration-/Bild-Layoutshift hinweg.
  scrollToHash();
};

// Auch bei Client-seitiger Navigation auf eine URL mit Hash anspringen.
export const onRouteUpdate = () => {
  scrollToHash();
};

export const wrapRootElement = ({ element }) => (
  <SegmentProvider>
    <TrackingProvider>
      {element}
    </TrackingProvider>
  </SegmentProvider>
)

// Survey (index) now shows on every session start
// Users will always see the survey when visiting the root path
// Previously stored segments are preserved but don't trigger auto-redirect
